import {
  ArrowLeftOutlined,
  CalendarOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form, Space, Tag, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  MODULES_KEY,
  PERMISSION_OBJECT,
  POD_TYPES,
  ROUTES,
  ROUTES_MODULES_KEY,
  SEARCH_PAGINATION_TABLE_FIELDS,
} from '../../../common/constants';
import CommonTable from '../../../components/CommonTable';
import SearchComponent from '../../../components/SearchComponent';
import VerifyPermissions from '../../../components/VerifyPermissions';
import { useTableQueryParams } from '../../../hooks/useTableQueryParams';
import PodModal from './components/PodModal';
import { CREATE_POD, UPDATE_POD } from './graphql/Mutations';
import { PODS } from './graphql/Queries';

const buildPodsFilter = (params, streamId) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
  streamIds: [streamId],
});

function Pods() {
  const history = useHistory();
  const { streamId } = useParams();
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const { params, setParams } = useTableQueryParams({
    fields: SEARCH_PAGINATION_TABLE_FIELDS,
  });

  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });

  const [fetchPods, { loading, data }] = useLazyQuery(PODS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [createPod, { loading: creating }] = useMutation(CREATE_POD, {
    onCompleted() {
      setShowModal(false);
      fetchPods({ variables: { filters: buildPodsFilter(params, streamId) } });
    },
  });

  const [updatePod, { loading: updating }] = useMutation(UPDATE_POD, {
    onCompleted() {
      setShowModal(false);
      setEditRecord(null);
      fetchPods({ variables: { filters: buildPodsFilter(params, streamId) } });
    },
  });

  useEffect(() => {
    fetchPods({ variables: { filters: buildPodsFilter(params, streamId) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, streamId]);

  const handleSearch = (value) => {
    setParams({ search: value?.trim(), page: 1 });
  };

  const handleTableChange = (pagination) => {
    const { current: currentPage, pageSize } = pagination;
    setParams({
      page: pageSize !== params?.pageSize ? 1 : currentPage,
      pageSize,
    });
  };

  const handleOpenCreate = () => {
    setEditRecord(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleOpenEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      podLeadId: record.podLeadId,
      isActive: record.isActive,
    });
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditRecord(null);
  };

  const handleSubmit = (values) => {
    if (editRecord) {
      updatePod({ variables: { where: { id: editRecord.id }, input: values } });
    } else {
      createPod({ variables: { input: { ...values, streamId } } });
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => text,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (val) => (
        <Tag>{POD_TYPES.find((t) => t.value === val)?.label || val}</Tag>
      ),
    },
    {
      title: 'Lead',
      key: 'podLead',
      render: (_, record) =>
        record.podLead
          ? `${record.podLead.firstName} ${record.podLead.lastName}`
          : '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val) => (
        <Tag color={val ? 'green' : 'red'}>{val ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          {isUpdatePermission && (
            <Tooltip title="Edit Pod">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(record);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="View in Team Calendar">
            <Button
              type="text"
              icon={<CalendarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                history.push({
                  pathname: ROUTES_MODULES_KEY?.TEAM_CALENDAR,
                  search: `?${new URLSearchParams({ podId: record?.id })?.toString()}`,
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const streamName =
    history.location.state?.streamName || data?.pods?.pods?.[0]?.stream?.name;

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              type="text"
              onClick={() => history.goBack()}
            />
            {streamName ? `${streamName} — Pods` : 'Pods'}
          </Space>
        }
        extra={
          <div className="listing-filter">
            {isCreatePermission && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreate}
              >
                New Pod
              </Button>
            )}
            <SearchComponent
              key={params?.search || ''}
              defaultValue={params?.search}
              getData={handleSearch}
              name="Pod"
            />
          </div>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            bordered
            columns={columns}
            data={data?.pods?.pods || []}
            loadingData={loading}
            onChange={handleTableChange}
            rowKey={(obj) => obj?.id}
            paginationConfig={{
              current: params?.page,
              total: data?.pods?.count,
              pageSize: params?.pageSize,
            }}
            onRow={(record) => ({
              onClick: () =>
                history.push(`${ROUTES?.AOS_STREAMS}/${record.id}/detail`, {
                  podName: record.name,
                }),
              style: { cursor: 'pointer' },
            })}
          />
        </div>

        <PodModal
          form={form}
          open={showModal}
          editRecord={editRecord}
          loading={creating || updating}
          onFinish={handleSubmit}
          onCancel={handleCancel}
        />
      </Card>
    </div>
  );
}

export default Pods;
